import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import env from 'env/env';
import root from 'window-or-global';
import classNames from 'classnames';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { TypographySecondary } from '@johnlewispartnership/wtr-ingredients/foundations/typography/TypographySecondary';
import Button from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import { trackRecipeClick, useTrackRecipesSearchPageLoad } from 'analytics/recipes-tracking';
import { recipeURL, recipeLandingJsonLd, selectLogo } from 'utils/format-recipe';
import { scrollIntoViewAndFocusElement } from 'utils/scroll';
import { getURLParamValue } from 'redux/modules/recipes/utils/get-url-param-value';
import { useRecipesLocation } from 'components/Recipes/utils/utils';
import type { RecipeSummaryItem } from 'api/definitions/recipes';
import { INCREMENT_CURRENT_RECIPES_PAGE } from 'redux/modules/cms-hybrid-recipes-page/actions/types';
import { NAV_TO_RECIPE } from 'redux/modules/recipes/actions/types';
import { getIsFiltered } from 'redux/modules/recipes/get-filtered-recipes';
import { filteredRecipes, RecipeSortType } from 'services/recipes-landing-service';
import { DEFAULT_SORT_BY } from 'redux/modules/recipes/constants';
import Filters from '../Filters';
import SortBy from '../SortBy';
import SearchBar from '../RecipeSearchBar';
import { RecipeCard } from '../RecipeCard';

import styles from './index.scss';

type RecipeCardsGridProps = {
  recipes: RecipeSummaryItem[];
  filters: any[];
  disableFilters?: boolean;
  disableSortBy?: boolean;
  defaultSortBy?: RecipeSortType;
  jwt: string;
  dispatch: (action: any) => void;
  tagList: string[];
  totaltaggedrecipes: number;
  currentPage: number;
  filteringOperation: boolean;
  paginatingOperation: boolean;
  loadingCount: number;
  isLastPage: boolean;
  gridType: string;
  gridTitle: string;
  isFiltersModalOpen: boolean;
  setFiltersModalOpen: (isOpen: boolean) => void;
};

const rememberFocus = (elementId: string) => {
  root.history?.replaceState({ recipe: elementId }, '', '');
};

const RecipeCardsGrid = ({
  recipes = [],
  filters = [],
  disableFilters = false,
  disableSortBy = false,
  defaultSortBy = DEFAULT_SORT_BY,
  dispatch,
  jwt = '',
  tagList = [],
  totaltaggedrecipes = 0,
  currentPage = 1,
  filteringOperation = false,
  paginatingOperation = false,
  loadingCount = 0,
  isLastPage = false,
  gridType = '',
  gridTitle = '',
  isFiltersModalOpen,
  setFiltersModalOpen,
}: RecipeCardsGridProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const focused = useRef<HTMLAnchorElement | null>(null);
  const location = useRecipesLocation();
  const [featuredRecipes, setFeaturedRecipes] = useState<RecipeSummaryItem[]>([]);
  const featuredGrid = gridType === 'featured';
  const focusedRecipe = root?.history?.state?.recipe;

  const toggleModal = useCallback(() => {
    setFiltersModalOpen(!isFiltersModalOpen);
  }, [setFiltersModalOpen, isFiltersModalOpen]);

  useEffect(() => {
    if (featuredGrid) {
      filteredRecipes(jwt, tagList, [], defaultSortBy, '', 0, 3).then(res => {
        if (res) {
          setFeaturedRecipes(res.recipes);
        }
      });
    }
  }, [featuredGrid, jwt, tagList, defaultSortBy]);

  useEffect(() => {
    if (!featuredGrid && focused?.current && focusedRecipe) {
      scrollIntoViewAndFocusElement(focused.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused?.current]);

  useTrackRecipesSearchPageLoad(!loadingCount, getURLParamValue('searchTerm'), totaltaggedrecipes);

  const [nCards, setNCards] = useState(0);

  useEffect(() => {
    if (!loadingCount) {
      setNCards(recipes?.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCount]);

  const handleLoadMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    searchParams.set('page', (currentPage + 1).toString());
    searchParams.set('startPage', '0');

    setSearchParams(searchParams);

    dispatch({
      type: INCREMENT_CURRENT_RECIPES_PAGE,
    });
  };

  const loadedRecipes = featuredGrid ? featuredRecipes : recipes.filter(Boolean);
  const haveNoRecipes = !recipes.length && !loadingCount && !filteringOperation;

  const showSearchBar = !location.isSearchPage;

  const haveFilters = getIsFiltered(new URLSearchParams(location.search));

  return (
    <div
      data-testid="recipe-grid"
      className={classNames(styles.recipegrid, {
        [styles.noSearchGrid]: !showSearchBar,
        [styles.featuredGrid]: featuredGrid,
      })}
    >
      {!featuredGrid && showSearchBar && !haveNoRecipes && (
        <div className={styles.search} data-testid="search-bar">
          <SearchBar placeholder="Search recipe..." />
        </div>
      )}
      {disableFilters ||
        (!haveNoRecipes && (
          <div className={styles.filters}>
            <Filters
              filters={filters}
              isFiltersModalOpen={isFiltersModalOpen}
              toggleModal={toggleModal}
            />
          </div>
        ))}
      <div
        data-testid="recipe-grid-inner"
        className={classNames(
          featuredGrid || haveNoRecipes ? styles['col-single'] : styles['col-2'],
          styles.keepGridHeight,
        )}
        style={loadingCount ? { minHeight: (nCards / 3) * (389 + 18) + 80 } : {}}
      >
        {disableSortBy ||
          (recipes?.length > 0 && (
            <div className={styles.sort} data-testid="sort-by">
              <SortBy defaultSortBy={defaultSortBy} />
            </div>
          ))}
        {featuredGrid && gridTitle && loadedRecipes.length > 0 && (
          <TypographySecondary
            data-testid="featured-title"
            component="h2"
            font="sebenta"
            size={28}
            className={styles.featuredTitle}
          >
            {gridTitle}
          </TypographySecondary>
        )}
        {recipes?.length === 0 && (
          <div data-testid="recipe-emptygrid" className={styles.emptygrid}>
            <div className={styles.emptygridinner}>
              {!haveFilters && !filteringOperation && (
                <>
                  <Typography styleAs="paragraphHeading">
                    Sorry, we currently have no recipes matching this page
                  </Typography>
                  <TextLink component={Link} to={`${env.root}/recipes/all-recipes`}>
                    <Button label="View all recipes" theme="secondary" />
                  </TextLink>
                </>
              )}
              {haveFilters && !filteringOperation && (
                <>
                  <Typography styleAs="paragraphHeading">
                    Sorry, we currently have no recipes matching your search
                  </Typography>
                  <TextLink component={Link} to={location.pathname}>
                    <Button label="Clear all filters" theme="secondary" />
                  </TextLink>
                </>
              )}
            </div>
          </div>
        )}
        <ul id="recipe-cards" className={styles.cards} data-testid="recipe-cards">
          {loadedRecipes.map((recipe, index) => {
            const analyticsName = `recipe-card-${recipe.id}`;
            const logo = selectLogo(recipe?.logos);
            const isMealMath = recipe?.isMealMath;

            return (
              <RecipeCard
                key={recipe.id}
                recipe={recipe}
                focusRef={(focusedRecipe === recipe.id && focused) || null}
                Before={
                  isMealMath &&
                  logo &&
                  (({ src, alt }) => (
                    <div className={styles.recipeLogoWrapper}>
                      <img src={src} alt={alt} className={styles.recipeLogo} loading="lazy" />
                    </div>
                  ))(logo)
                }
                imgProps={{ loading: index > 2 ? 'lazy' : undefined }}
                analytics={{
                  name: analyticsName,
                  position: String(index + 1),
                  'component-type': 'internal-promotion',
                  'asset-module-type': 'recipes',
                  'enhanced-ecommerce-internal-promotion': 'true',
                }}
                onClick={() => {
                  dispatch({ type: NAV_TO_RECIPE, recipe });
                  rememberFocus(recipe.id);
                  trackRecipeClick(recipe.id, analyticsName, String(index + 1), recipeURL(recipe));
                }}
              />
            );
          })}
        </ul>

        {!featuredGrid && !isLastPage && (
          <div style={{ textAlign: 'center', paddingBottom: '40px' }} data-testid="pagination">
            {paginatingOperation ? (
              <Spinner isActive />
            ) : (
              <Button
                theme="secondary"
                id="loadmore-button"
                onClick={e => handleLoadMore(e)}
                data-next={parseInt(currentPage.toString(), 10) + 1}
                data-analytics-name="loadmore-button"
                data-analytics-next={parseInt(currentPage.toString(), 10) + 1}
                label="Load more"
              />
            )}
            <noscript data-testid="noscript">
              <a
                href={`${location.pathname}?page=${currentPage + 1}`}
                id="loadmore-link"
                data-next={parseInt(currentPage.toString(), 10) + 1}
              >
                Load more
              </a>
            </noscript>
          </div>
        )}
      </div>
      <Helmet>
        <script type="application/ld+json" data-test="seo-meta-data-recipelist">
          {JSON.stringify(recipeLandingJsonLd(loadedRecipes))}
        </script>
      </Helmet>
    </div>
  );
};

export default RecipeCardsGrid;
